<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';

const props = defineProps({
    openOnLoad: {
        type: Boolean,
        default: false,
    },
});

const specialists = [
    {
        key: 'stella',
        bgClass: 'bg-aiAgentWebDesigner',
        buttonClass: 'bg-aiAgentWebDesigner text-gray-900',
        link: '/page/create?title=Home&ai_page=1',
    },
    {
        key: 'susan',
        bgClass: 'bg-aiAgentDataAnalyst',
        buttonClass: 'bg-aiAgentDataAnalyst text-gray-900',
        link: '/bo/susan',
    },
    {
        key: 'tom',
        bgClass: 'bg-aiAgentRelationshipManager',
        buttonClass: 'bg-aiAgentRelationshipManager text-gray-900',
        link: '/bo/tom',
    },
    {
        key: 'maya',
        bgClass: 'bg-aiAgentContentCreator',
        buttonClass: 'bg-aiAgentContentCreator text-gray-900',
        link: '/bo/ai-content-creator',
    },
    {
        key: 'alex',
        bgClass: 'bg-aiAgentNewsletterSpecialist',
        buttonClass: 'bg-aiAgentNewsletterSpecialist text-gray-900',
        link: '/bo/alex',
    },
    {
        key: 'nina',
        bgClass: 'bg-aiAgentSalesAssistant',
        buttonClass: 'bg-aiAgentSalesAssistant text-gray-900',
        link: null,
        action: 'slideout',
    },
    {
        key: 'oscar',
        bgClass: 'bg-aiAgentLeadFinder',
        buttonClass: 'bg-aiAgentLeadFinder text-gray-900',
        link: '/bo/hugo',
    },
    {
        key: 'leo',
        bgClass: 'bg-aiAgentAutomationExpert',
        buttonClass: 'bg-aiAgentAutomationExpert text-gray-900',
        link: '/bo/leo',
    },
];

const hired = ref([]);
const slotLimit = ref(1);
const remainingSlots = ref(0);
const showModal = ref(false);
const loading = ref(true);
const showNinaStandalone = ref(false);

const hiredSpecialists = computed(() => {
    return specialists.filter(s => hired.value.includes(s.key));
});

const nonHiredSpecialists = computed(() => {
    return specialists.filter(s => !hired.value.includes(s.key));
});

async function fetchTeamState() {
    try {
        const response = await axios.get('/bo/ai-team');
        hired.value = response.data.hired;
        slotLimit.value = response.data.slot_limit;
        remainingSlots.value = response.data.remaining_slots;
    } catch (e) {
        // Fallback
        hired.value = ['stella'];
    } finally {
        loading.value = false;
    }
}

function openModal() {
    showModal.value = true;
}

function onTeamUpdated(data) {
    hired.value = data.hired;
    slotLimit.value = data.slot_limit;
    remainingSlots.value = data.remaining_slots;
    window.dispatchEvent(new CustomEvent('dashboard-ai-team-updated', { detail: data }));
}

function closeModal() {
    showModal.value = false;
    window.dispatchEvent(new CustomEvent('dashboard-ai-team-updated', {
        detail: {
            hired: hired.value,
            slot_limit: slotLimit.value,
            remaining_slots: remainingSlots.value,
        },
    }));
}

onMounted(() => {
    window.addEventListener('open-dashboard-hiring-modal', openModal);

    fetchTeamState().then(() => {
        if (props.openOnLoad) {
            openModal();
        }
    });
});

onUnmounted(() => {
    window.removeEventListener('open-dashboard-hiring-modal', openModal);
});
</script>

<template>
    <div class="mb-4 space-y-4 @container">
        <!-- Flowie orchestrator chat -->
        <dashboard-flowie-chat data-flowie-chat></dashboard-flowie-chat>

        <!-- Specialists grid -->
        <div class="flex items-center justify-between">
            <h2 class="text-xl font-semibold text-gray-800">
                {{ _mft('dashboard:aiSpecialists.sectionTitle') }}
            </h2>
            <button-secondary
                size="sm"
                data-manage-team
                @click="openModal"
            >
                {{ _mft('dashboard:aiSpecialists.manageTeam') }}
            </button-secondary>
        </div>

        <div v-if="!loading" class="grid grid-cols-2 gap-4 @md:grid-cols-4">
            <!-- Hired specialists -->
            <card-basic
                v-for="specialist in hiredSpecialists"
                :key="specialist.key"
                margin-bottom-class=""
                :compact="true"
                class="flex items-stretch justify-center"
            >
                <component
                    :is="specialist.link ? 'a' : specialist.action ? 'button' : 'div'"
                    :href="specialist.link || undefined"
                    :class="(specialist.link || specialist.action) ? 'cursor-pointer' : 'cursor-default'"
                    class="group h-full flex flex-col justify-between text-center"
                    @click="specialist.action === 'slideout' ? showNinaStandalone = true : null"
                >
                    <div class="flex flex-col items-center">
                        <div
                            class="flex items-center justify-center w-12 h-12 mb-3 rounded-full group-hover:scale-125 transition-all duration-300"
                            :class="specialist.bgClass"
                        >
                            <!-- Stella: Web Designer -->
                            <div v-if="specialist.key === 'stella'">
                                <ai-agent-web-designer-avatar class="w-full h-full" />
                            </div>
                            <!-- Susan: Data Analyst -->
                            <div v-else-if="specialist.key === 'susan'">
                                <ai-agent-data-analyst-avatar class="w-full h-full" />
                            </div>
                            <!-- Tom: Relationship Manager -->
                            <div v-else-if="specialist.key === 'tom'">
                                <ai-agent-relationship-manager-avatar class="w-full h-full" />
                            </div>
                            <!-- Maya: Content Creator -->
                            <div v-else-if="specialist.key === 'maya'">
                                <ai-agent-content-creator-avatar class="w-full h-full" />
                            </div>
                            <!-- Alex: Newsletter Specialist -->
                            <div v-else-if="specialist.key === 'alex'">
                                <ai-agent-newsletter-specialist-avatar class="w-full h-full" />
                            </div>
                            <!-- Nina: Sales Assistant -->
                            <div v-else-if="specialist.key === 'nina'">
                                <ai-agent-sales-assistant-avatar class="w-full h-full" />
                            </div>
                            <!-- Oscar: Lead Finder -->
                            <div v-else-if="specialist.key === 'oscar'">
                                <ai-agent-lead-finder-avatar class="w-full h-full" />
                            </div>
                            <!-- Leo: Automation Expert -->
                            <div v-else-if="specialist.key === 'leo'">
                                <ai-agent-automation-expert-avatar class="w-full h-full" />
                            </div>
                        </div>

                        <h3 class="text-xs font-bold text-gray-800">
                            {{ _mft('dashboard:aiSpecialists.' + specialist.key + '.role') }}
                        </h3>
                        <p class="text-xs text-gray-500 mb-1">
                            {{ _mft('dashboard:aiSpecialists.' + specialist.key + '.name') }}
                        </p>
                        <p class="my-1 text-xxs text-gray-400">{{ _mft('dashboard:aiSpecialists.' + specialist.key + '.tagline') }}</p>
                    </div>
                </component>
            </card-basic>
        </div>

        <!-- Non-hired specialists are informational only -->
        <div
            v-if="!loading && nonHiredSpecialists.length"
            class="grid grid-cols-2 gap-4 @md:grid-cols-4"
        >
            <div
                v-for="specialist in nonHiredSpecialists"
                :key="'not-hired-' + specialist.key"
                class="flex min-w-0 w-full items-center gap-2 border border-gray-200 rounded-lg bg-gray-50 px-2 py-2 opacity-70 cursor-default"
                @click="openModal"
            >
                <div
                    class="flex items-center justify-center w-8 h-8 rounded-full shrink-0"
                    :class="specialist.bgClass"
                >
                    <div v-if="specialist.key === 'stella'">
                        <ai-agent-web-designer-avatar class="w-full h-full" />
                    </div>
                    <div v-else-if="specialist.key === 'susan'">
                        <ai-agent-data-analyst-avatar class="w-full h-full" />
                    </div>
                    <div v-else-if="specialist.key === 'tom'">
                        <ai-agent-relationship-manager-avatar class="w-full h-full" />
                    </div>
                    <div v-else-if="specialist.key === 'maya'">
                        <ai-agent-content-creator-avatar class="w-full h-full" />
                    </div>
                    <div v-else-if="specialist.key === 'alex'">
                        <ai-agent-newsletter-specialist-avatar class="w-full h-full" />
                    </div>
                    <div v-else-if="specialist.key === 'nina'">
                        <ai-agent-sales-assistant-avatar class="w-full h-full" />
                    </div>
                    <div v-else-if="specialist.key === 'oscar'">
                        <ai-agent-lead-finder-avatar class="w-full h-full" />
                    </div>
                    <div v-else-if="specialist.key === 'leo'">
                        <ai-agent-automation-expert-avatar class="w-full h-full" />
                    </div>
                </div>
                <div class="min-w-0">
                    <p class="text-xxs font-semibold text-gray-900 truncate mb-0">
                        {{ _mft('dashboard:aiSpecialists.' + specialist.key + '.role') }}
                    </p>
                    <p class="text-[9px] truncate mb-0 text-gray-700">
                        {{ _mft('dashboard:aiSpecialists.' + specialist.key + '.name') }}
                        · <span class="font-medium text-black">{{ _mft('dashboard:aiSpecialists.notHired') }}</span>
                    </p>
                </div>
            </div>
        </div>

        <!-- Hiring modal -->
        <dashboard-hiring-modal
            v-if="showModal"
            :hired="hired"
            :slot-limit="slotLimit"
            :specialists="specialists"
            @close="closeModal"
            @updated="onTeamUpdated"
        />

        <!-- Nina standalone slideout -->
        <nina-standalone-slideout
            v-if="showNinaStandalone"
            @close="showNinaStandalone = false"
        />
    </div>
</template>
